import { clsx } from 'clsx';
import React, { useMemo } from 'react';

import type { AdComponent } from '../../../../../../core/data-sources/content-components/types.js';
import type { TeaserListProps } from '../../../../components/teasers/TeaserList.js';
import type { TeaserWithCoords, TopListView } from '../types.js';

import { useSessionData } from '../../../../../../../public-src/core/js/schibsted-account/react.server.js';
import { useByfunnPulseContext } from '../../../../../pulse/index.js';
import Ad from '../../../../components/ads/Ad/Ad.js';
import { useRenderContext } from '../../../../contexts/RenderContext.js';
import useInitializeAds from '../../../../hooks/useInitAds.js';
import { insertAds } from '../../utils/ads.js';
import { TopListRelatedTeaser } from './TopListRelatedTeaser.js';
import { TopListTeaser } from './TopListTeaser.js';

type TopListTeaserListProps = {
  teasers: TeaserWithCoords[];
  view: TopListView;
  teaserRefs: React.RefObject<Record<string, HTMLDivElement | null>>;
  relatedTopListTeasers?: TeaserListProps[];
  onTeaserView?: (articleId: string) => void;
  onRelatedTopListTeaserView?: () => void;
};

const TopListTeaserList: React.FC<TopListTeaserListProps> = ({
  teasers,
  view,
  teaserRefs,
  relatedTopListTeasers,
  onTeaserView,
  onRelatedTopListTeaserView,
}) => {
  const { siteVersion, isMobile } = useRenderContext();

  const teasersWithAds = useMemo(
    () => insertAds(teasers, isMobile),
    [isMobile, teasers],
  );

  const { getEnvironmentId } = useByfunnPulseContext();

  const session = useSessionData();

  useInitializeAds(getEnvironmentId, session, siteVersion);

  let position = 0;

  const renderComponent = (
    comp: AdComponent | TeaserWithCoords,
  ): React.ReactNode => {
    if (comp.type === 'ad') {
      return (
        <div
          key={`ad-wrapper-${comp.id}`}
          className={clsx('top-list__ad-wrapper', {
            'top-list__ad-wrapper--hidden': view === 'map',
          })}
        >
          <Ad
            id={comp.id}
            cssClass={comp.cssClass}
            isMobile
            containerClass="ad-list"
          />
        </div>
      );
    }

    position += 1;

    return (
      <TopListTeaser
        key={comp.id}
        teaser={{
          ...comp,
          pulseData: {
            contentSource: 'Editorial',
            placement: {
              primaryAxis: view === 'map' ? 'Horizontal' : 'Vertical',
              primaryPosition: position,
            },
          },
        }}
        view={view}
        onView={onTeaserView}
        ref={(el) => {
          teaserRefs.current[comp.id] = el;
        }}
      />
    );
  };

  return (
    <div className={`top-list__teaser-list top-list__teaser-list--${view}`}>
      {teasersWithAds.map(renderComponent)}
      {relatedTopListTeasers?.map((teaser, index) => (
        <TopListRelatedTeaser
          key={teaser.id}
          teaser={{
            ...teaser,
            pulseData: {
              contentSource: 'Editorial',
              placement: {
                containerName: 'related-top-lists',
                primaryAxis: view === 'map' ? 'Horizontal' : 'Vertical',
                positionInBundle: index + 1,
              },
              content: {
                format: 'Other',
                formatSubtype: 'Unknown',
              },
            },
          }}
          view={view}
          onView={onRelatedTopListTeaserView}
        />
      ))}
    </div>
  );
};

export { TopListTeaserList };
